// Builds the in-memory file map for an exported character npm package.
// Pure function of its inputs — no fetching, no DOM — so it is unit-testable
// directly (see tests/unit/characterPackage.test.mjs).
//
// input: {
//   id, name, version,
//   behaviorJson,            // from compileCharacterBehavior
//   runtimeJs,               // string contents of the bundled runtime (caller reads dist file)
//   states, clips,           // arrays from compiler
//   variableDefaults,        // { name: value } (may be {})
//   rigBytes,                // Uint8Array
//   clipFiles,               // [{ name: 'idle_balanced.glb', bytes: Uint8Array }]
//   materialsJson,           // object (material_assets rows) — may be null
//   textures,                // [{ id, ext, bytes }] — may be []
//   materialMappingJson,     // object — may be null
// }
// returns: { packageName: '@avataros/character-<id>', files: [{ name, data: Uint8Array }] }
export function buildCharacterPackage(input) {
  const enc = new TextEncoder()
  const packageName = `@avataros/character-${input.id}`

  // ── manifest.json ────────────────────────────────────────────────────────────
  const manifest = {
    schemaVersion: '1.0.0',
    kind: 'character',
    name: input.name,
    id: input.id,
    states: input.states,
    clips: input.clips,
    files: {
      rig: 'assets/rig.glb',
      behavior: 'behavior.json',
      runtime: 'runtime/runtime.js',
    },
    api: {
      class: 'AvatarCharacter',
      methods: [
        { name: 'setState',      signature: 'setState(name)' },
        { name: 'playAnimation', signature: 'playAnimation(name)' },
        { name: 'setVariable',   signature: 'setVariable(name,value)' },
        { name: 'update',        signature: 'update(dt)' },
      ],
    },
  }

  // ── package.json ─────────────────────────────────────────────────────────────
  // Scoped name matches `packageName` and the import shown in llms.txt. The
  // zero-private-dep guarantee is about DEPENDENCIES (there are none, only a
  // `three` peer dep) — the scoped *name* is fine and intended.
  const packageJson = {
    name: packageName,
    version: input.version,
    description: `AvatarOS exported character "${input.name}" — compiled behavior + assets.`,
    type: 'module',
    main: 'index.js',
    types: 'index.d.ts',
    exports: { '.': './index.js' },
    files: ['index.js', 'index.d.ts', 'manifest.json', 'llms.txt', 'behavior.json', 'runtime', 'assets'],
    peerDependencies: { three: '>=0.150' },
  }

  // ── index.js ─────────────────────────────────────────────────────────────────
  // Bake literals into the template so the emitted file is self-contained.
  const statesList = (input.states ?? []).map(s => `  //   '${s}'`).join('\n')
  const clipsList  = (input.clips  ?? []).map(c => `  //   '${c}'`).join('\n')

  // JSON-serialise the data we bake in; stringify is safe here — the values are
  // plain primitives/objects/arrays coming from the compiler.
  const bakedBehavior         = JSON.stringify(input.behaviorJson ?? {})
  const bakedStates           = JSON.stringify(input.states ?? [])
  const bakedClips            = JSON.stringify(input.clips ?? [])
  const bakedVariableDefaults = JSON.stringify(input.variableDefaults ?? {})

  // Find the first stateMachine node id in the behavior so setState can target it.
  // Baked as a literal string (or null) to avoid a runtime scan.
  const smNode = (input.behaviorJson?.nodes ?? []).find(n => n.type === 'stateMachine')
  const bakedSmNodeId = JSON.stringify(smNode?.id ?? null)

  const indexJs = [
    '// AvatarCharacter — generated by AvatarOS character export (Task 8).',
    '// Self-contained: behavior, states, and variable defaults are baked in as literals.',
    '// Only dependency: the co-packaged runtime (relative import, no private registry).',
    "import { RUNTIME_VERSION, evaluateBody, createSMState, applyWeights } from './runtime/runtime.js'",
    "import * as THREE from 'three'",
    "import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'",
    "import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'",
    '',
    'export { RUNTIME_VERSION }',
    '',
    '// ── Baked-in behavior and metadata ──────────────────────────────────────────',
    '// These are serialised at export time and never fetched from the network.',
    'const behavior         = ' + bakedBehavior + '',
    'const states           = new Set(' + bakedStates + ')',
    'const clips            = new Set(' + bakedClips + ')',
    'const variableDefaults = ' + bakedVariableDefaults + '',
    '// ID of the stateMachine node in the behavior graph (null if none).',
    'const SM_NODE_ID       = ' + bakedSmNodeId + '',
    '',
    '/**',
    ' * AvatarCharacter — compiled character behavior package.',
    ' *',
    ' * Extends THREE.Group so you can add it directly to a scene.',
    ' * Call `await character.ready` before rendering.',
    ' *',
    ' * Available states:',
    statesList,
    ' *',
    ' * Available clips:',
    clipsList,
    ' */',
    'export class AvatarCharacter extends THREE.Group {',
    '  /**',
    '   * @param {{',
    '   *   dracoPath?: string,   // CDN path for DRACOLoader decoder (default: Google CDN)',
    '   *   variables?: Record<string,unknown>,  // override variable defaults',
    '   * }} opts',
    '   */',
    '  constructor(opts = {}) {',
    '    super()',
    '',
    '    // Runtime context — module-level singletons, NOT React state.',
    '    // Mirrors the app\'s rt object passed to evaluateBody each frame.',
    '    this._rt = {',
    '      now:        () => performance.now(),',
    '      variables:  Object.assign({}, variableDefaults, opts.variables ?? {}),',
    '      sticky:     new Map(),',
    '      valueCache: new Map(),',
    '      sm:         {},',
    '    }',
    '',
    '    this._mixer   = null',
    '    this._actions = {}',
    '',
    '    // One-shot override: set by playAnimation(), cleared when the clip ends.',
    '    // While active, update() skips the compiled behavior and plays this clip at weight 1.',
    '    this._directClip = null',
    '',
    '    // Kick off async loading.  Consumers: `await character.ready` before rendering.',
    '    this.ready = this._load(opts)',
    '  }',
    '',
    '  // ── Asset loading ──────────────────────────────────────────────────────────',
    '',
    '  async _load(opts) {',
    '    const dracoPath = opts.dracoPath ?? \'https://www.gstatic.com/draco/versioned/decoders/1.5.6/\'',
    '',
    '    const dracoLoader = new DRACOLoader()',
    '    dracoLoader.setDecoderPath(dracoPath)',
    '',
    '    const loader = new GLTFLoader()',
    '    loader.setDRACOLoader(dracoLoader)',
    '',
    '    // Resolve asset URLs relative to this module so the package works when',
    '    // installed to node_modules or served from a CDN base.',
    '    const rigUrl = new URL(\'./assets/rig.glb\', import.meta.url).href',
    '    const gltf   = await loader.loadAsync(rigUrl)',
    '    this.add(gltf.scene)',
    '',
    '    // Create the mixer on the rig scene.',
    '    this._mixer = new THREE.AnimationMixer(gltf.scene)',
    '',
    '    // Load each clip GLB and register an action.',
    '    await Promise.all([...clips].map(async clipName => {',
    '      const url  = new URL(\'./assets/clips/\' + clipName + \'.glb\', import.meta.url).href',
    '      const cGltf = await loader.loadAsync(url)',
    '      const clip  = cGltf.animations[0]',
    '      if (clip) {',
    '        clip.name = clipName',
    '        this._actions[clipName] = this._mixer.clipAction(clip)',
    '      }',
    '    }))',
    '',
    '    // Material binding (best-effort Slice 1).',
    '    // If materials.json + material-mapping.json are present in the package,',
    '    // a full buildMaterialsFromAssets/applyMaterialMapping pass would go here.',
    '    // For now we rely on the materials baked into the rig GLB itself.',
    '    // TODO (Slice 2): load assets/materials.json + assets/material-mapping.json',
    '    //   and call the runtime material builder so consumer scenes match the',
    '    //   AvatarOS editor exactly.',
    '',
    '    dracoLoader.dispose()',
    '  }',
    '',
    '  // ── Public API ─────────────────────────────────────────────────────────────',
    '',
    '  /**',
    '   * Transition the state machine to a named state.',
    '   *',
    '   * Implementation notes (Slice 1):',
    '   *   The compiled behavior\'s SM state lives in `this._rt.sm[SM_NODE_ID]`.',
    '   *   We directly write a transition object (from current → target, 0.3 s crossfade)',
    '   *   into that slot, mirroring what updateTransitions() does when a rule fires.',
    '   *   This guarantees a smooth crossfade without depending on a specific rule being',
    '   *   true in the behavior graph.  The next `update()` call will drive evalState',
    '   *   which picks up the transition and blends accordingly.',
    '   *',
    '   * @param {string} name',
    '   */',
    '  setState(name) {',
    '    if (!states.has(name)) throw new Error(\'AvatarCharacter: unknown state "\' + name + \'"\')',
    '    if (!SM_NODE_ID) return  // no SM in this behavior — no-op',
    '',
    '    // Ensure SM state exists (mirrors createSMHandler lazy init in sm.js)',
    '    if (!this._rt.sm[SM_NODE_ID]) this._rt.sm[SM_NODE_ID] = createSMState()',
    '    const smState = this._rt.sm[SM_NODE_ID]',
    '',
    '    // Already in this state and not mid-transition — nothing to do.',
    '    if (smState.currentState === name && !smState.transition) return',
    '',
    '    const fromPickedClip = smState.pickedClip ?? null',
    '',
    '    // Find the target state node to pick its clip.',
    '    const smNode = behavior.nodes?.find(n => n.id === SM_NODE_ID)',
    '    const innerNodes = smNode?.data?.innerNodes ?? []',
    '    const toStateNode = innerNodes.find(n => n.id === name)',
    '',
    '    // Pick the clip for the new state (mirrors pickClipForState in sm.js).',
    '    const clips_ = toStateNode?.data?.clips',
    '    const toPickedClip = Array.isArray(clips_) && clips_.length > 0',
    '      ? clips_[Math.floor(Math.random() * clips_.length)]',
    '      : (toStateNode?.data?.clipName ?? null)',
    '',
    '    smState.transition = {',
    '      from:          smState.currentState ?? name,',
    '      to:            name,',
    '      startTime:     this._rt.now(),',
    '      duration:      300,',
    '      fromPickedClip,',
    '    }',
    '    smState.currentState   = name',
    '    smState.stateEnteredAt = this._rt.now()',
    '    smState.pickedClip     = toPickedClip',
    '  }',
    '',
    '  /**',
    '   * Play a named clip as a one-shot override.',
    '   *',
    '   * Implementation notes (Slice 1):',
    '   *   Sets `this._directClip` to the clip name.  The `update()` method stops all',
    '   *   other actions and plays this clip at weight 1, exactly like the app\'s',
    '   *   __DIRECT_PLAY__ mechanism.  The override self-clears when the action reports',
    '   *   it is no longer running (i.e. the clip has finished playing once).',
    '   *',
    '   * @param {string} name',
    '   */',
    '  playAnimation(name) {',
    '    if (!clips.has(name)) throw new Error(\'AvatarCharacter: unknown clip "\' + name + \'"\')',
    '    this._directClip = name',
    '    const action = this._actions[name]',
    '    if (action) action.reset().setEffectiveWeight(1).setLoop(2200 /* LoopOnce */, 1).play()',
    '  }',
    '',
    '  /**',
    '   * Write a variable into the runtime context.',
    '   * @param {string} name',
    '   * @param {unknown} value',
    '   */',
    '  setVariable(name, value) {',
    '    this._rt.variables[name] = value',
    '  }',
    '',
    '  /**',
    '   * Advance the runtime one tick.',
    '   *',
    '   * `dt` is elapsed time in **milliseconds** since the last call.',
    '   * THREE.AnimationMixer.update() expects seconds, so we divide by 1000.',
    '   * Call from your render loop:',
    '   *   renderer.setAnimationLoop((time, delta) => { character.update(delta); ... })',
    '   * (setAnimationLoop passes delta in ms when using the two-arg signature.)',
    '   *',
    '   * @param {number} dt — elapsed ms since last call',
    '   */',
    '  update(dt) {',
    '    // One-shot direct-play override: if active and the action is done, clear it.',
    '    if (this._directClip) {',
    '      const a = this._actions[this._directClip]',
    '      if (!a || !a.isRunning()) this._directClip = null',
    '    }',
    '',
    '    if (!this._directClip) {',
    '      // Normal path: evaluate the compiled behavior graph.',
    '      this._rt.valueCache.clear()',
    '      const out = evaluateBody(behavior, this._rt)',
    '      applyWeights(out.weights, out.loopSettings, out.speedSettings, this._actions)',
    '    }',
    '',
    '    // Advance the mixer (THREE expects seconds).',
    '    this._mixer?.update(dt / 1000)',
    '  }',
    '}',
    '',
  ].join('\n')

  // ── index.d.ts ───────────────────────────────────────────────────────────────
  const indexDts = `export declare const RUNTIME_VERSION: string

export declare class AvatarCharacter {
  /** Resolves once the rig GLB and all clip GLBs have loaded. */
  readonly ready: Promise<void>
  constructor(opts?: {
    /** Path to the DRACOLoader decoder directory (default: Google CDN 1.5.6). */
    dracoPath?: string
    /** Override compiled variable defaults. */
    variables?: Record<string, unknown>
  })
  /** Transition to a named state (throws if the state is not in the compiled behavior). */
  setState(name: string): void
  /** Play a named clip as a one-shot override (throws if clip is unknown). */
  playAnimation(name: string): void
  /** Write a variable into the runtime context. */
  setVariable(name: string, value: unknown): void
  /**
   * Advance one tick. dt is elapsed time in milliseconds.
   * Call from renderer.setAnimationLoop((time, delta) => character.update(delta)).
   */
  update(dt: number): void
}
`

  // ── llms.txt ─────────────────────────────────────────────────────────────────
  const statesBlock = (input.states ?? []).join(', ')
  const clipsBlock  = (input.clips  ?? []).join(', ')

  const llms = `# ${packageName}

AvatarOS exported character — compiled behavior + assets.
Zero-backend, zero-private-registry: installs from npm with only 'three' as a peer dependency.

## Install
npm install ${packageName}

## Quick start
import * as THREE from 'three'
import { AvatarCharacter } from '${packageName}'

const character = new AvatarCharacter()   // loads rig + clips + materials from the package
scene.add(character)
await character.ready

character.setState('${(input.states ?? [])[0] ?? 'Idle'}')   // drive the compiled state machine

// Drive the render loop
renderer.setAnimationLoop((time, delta) => {
  character.update(delta)                 // evaluate compiled behavior + advance the mixer
  renderer.render(scene, camera)
})

## API
- setState(name)         — transition to a state: ${statesBlock}
- playAnimation(name)    — play a clip: ${clipsBlock}
- setVariable(name, val) — write a behavior variable
- update(dt)             — advance one tick (dt in ms); call every frame

## Peer dependencies
- three >=0.150  (NOT bundled — bring your own)

## Notes
- This package contains compiled behavior + assets only. No network calls, no backend.
- The runtime is pre-bundled as runtime/runtime.js (ESM, imports only 'three').
- All asset paths are relative to the package root (use import.meta.url or a CDN base).
`

  // ── README.md ─────────────────────────────────────────────────────────────────
  const readme = `# ${packageName}\n\nExported AvatarOS character "${input.name}". See llms.txt for usage.\n`

  // ── Assemble files ────────────────────────────────────────────────────────────
  const files = [
    { name: 'package/package.json',        data: enc.encode(JSON.stringify(packageJson, null, 2)) },
    { name: 'package/manifest.json',       data: enc.encode(JSON.stringify(manifest, null, 2)) },
    { name: 'package/llms.txt',            data: enc.encode(llms) },
    { name: 'package/README.md',           data: enc.encode(readme) },
    { name: 'package/behavior.json',       data: enc.encode(JSON.stringify(input.behaviorJson)) },
    { name: 'package/runtime/runtime.js',  data: enc.encode(input.runtimeJs) },
    { name: 'package/index.js',            data: enc.encode(indexJs) },
    { name: 'package/index.d.ts',          data: enc.encode(indexDts) },
    { name: 'package/assets/rig.glb',      data: input.rigBytes },
  ]

  // Clip GLBs
  for (const clip of input.clipFiles ?? []) {
    files.push({ name: `package/assets/clips/${clip.name}`, data: clip.bytes })
  }

  // Optional materials / mapping
  if (input.materialsJson != null) {
    files.push({ name: 'package/assets/materials.json', data: enc.encode(JSON.stringify(input.materialsJson)) })
  }
  if (input.materialMappingJson != null) {
    files.push({ name: 'package/assets/material-mapping.json', data: enc.encode(JSON.stringify(input.materialMappingJson)) })
  }

  // Textures
  for (const tex of input.textures ?? []) {
    files.push({ name: `package/assets/textures/${tex.id}.${tex.ext}`, data: tex.bytes })
  }

  return { packageName, files }
}
